/**
 * sirus/BuffBar — R232q21 Phase 4 (Sirus 1:1 UI rebuild).
 *
 * Two stacked rows under the minimap:
 *   – BuffBar:    32×32 icons, max 16 visible (overflow hidden).
 *   – DebuffBar:  36×36 icons, red border tint, slightly larger so the
 *                 player notices threats faster (Sirus parity).
 *
 * Each cell shows:
 *   – Icon glyph (placeholder until DBC icon URLs land).
 *   – Stack count badge (top-right) when stacks > 1.
 *   – Time-remaining ring — same conic-gradient sweep as ActionBar's
 *     cooldown overlay so the visual language stays consistent.
 *   – On hover: re-uses `UnitTooltip` if available; otherwise relies on
 *     the native `title` attribute. Hover-tracking is local React state.
 *
 * Right-click → cancel buff. Emits a `aiwari-cancel-buff` window event
 * (`detail: { auraId }`) so the game-loop layer can decide whether to
 * round-trip a `cancel-aura` packet to the server. We deliberately do
 * NOT touch network code from a HUD component.
 *
 * Sort modes (cycle button at top of bar):
 *   – time   → soonest expiring first (default)
 *   – alpha  → name A-Я
 *   – applied→ insertion order (server array order)
 *
 * Position: fixed top-right, under the Minimap (top: 16, right: 16).
 *   buffs row at top, debuffs row beneath with 8px gap.
 *
 * Source: `useNet().players[selfId].auras` if such a field ever lands.
 * Today the schema only exposes `buffs` (id + endsAt + power). To stay
 * forward-compat we read both — preferring `auras` when present and
 * falling back to `buffs` mapped onto a normalised view model.
 *
 * z-index: 30 (under modals, above world).
 *
 * DEFERRED:
 *   – Real `auras` schema replication (see RemotePlayer.buffs in net-store).
 *   – Real DBC icon URLs (placeholder glyph for now).
 *   – Differentiating buff/debuff via dispelType once the field exists;
 *     today every entry without dispel info is treated as a buff.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { useNet, type RemotePlayer } from '../../state/net-store.js';

/* ─────────────────────────────────────────────────────────────────────
 * Aura view-model.
 * ──────────────────────────────────────────────────────────────────── */

type DispelType = 'magic' | 'curse' | 'disease' | 'poison' | 'physical' | 'enrage' | null;

interface AuraView {
  id: string;
  name: string;
  icon: string;
  stacks: number;
  expiresAt: number; // ms epoch
  durationMs: number;
  isDebuff: boolean;
  dispelType: DispelType;
  appliedIndex: number;
}

const MAX_BUFFS = 16;
const MAX_DEBUFFS = 16;
/**
 * Buff/debuff icon sizes are now rubber-UI tokens. Buff = slot-micro
 * (clamp 22–36px), Debuff = slot (clamp 28–44px) — keeps debuffs visibly
 * larger than buffs at every viewport size so the player notices threats
 * faster (Sirus parity). The strings are CSS values, not numbers, because
 * downstream we need them to flow through to inline `width:` directly.
 */
const BUFF_ICON = 'var(--aiwari-slot-micro)';
const DEBUFF_ICON = 'var(--aiwari-slot)';

const DISPEL_COLOURS: Record<NonNullable<DispelType>, string> = {
  magic:    'hsl(220 80% 65%)',
  curse:    'hsl(280 70% 65%)',
  disease:  'hsl(90 50% 55%)',
  poison:   'hsl(120 70% 50%)',
  physical: 'hsl(20 70% 55%)',
  enrage:   'hsl(0 80% 60%)',
};

type SortMode = 'time' | 'alpha' | 'applied';

const SORT_LABEL: Record<SortMode, string> = {
  time: 'по времени',
  alpha: 'по алфавиту',
  applied: 'по порядку',
};

/** Normalise whatever the server sent us into the AuraView shape. */
function normaliseAuras(player: RemotePlayer | undefined): AuraView[] {
  if (!player) return [];
  type Loose = {
    auras?: Array<{
      id?: string;
      name?: string;
      icon?: string;
      stacks?: number;
      expiresAt?: number;
      durationMs?: number;
      dispelType?: DispelType;
      isDebuff?: boolean;
    }>;
    buffs?: Array<{ id: string; endsAt: number; power?: number }>;
  };
  const loose = player as unknown as Loose;
  if (Array.isArray(loose.auras)) {
    const now = Date.now();
    return loose.auras
      .filter((a) => a && typeof a.id === 'string')
      .map((a, idx) => ({
        id: a.id ?? `aura-${idx}`,
        name: a.name ?? a.id ?? 'Эффект',
        icon: a.icon ?? '✦',
        stacks: typeof a.stacks === 'number' && a.stacks > 0 ? a.stacks : 1,
        expiresAt: typeof a.expiresAt === 'number' ? a.expiresAt : now + 5000,
        durationMs: typeof a.durationMs === 'number' ? a.durationMs : 5000,
        isDebuff: !!a.isDebuff,
        dispelType: a.dispelType ?? null,
        appliedIndex: idx,
      }));
  }
  if (Array.isArray(loose.buffs)) {
    const now = Date.now();
    return loose.buffs.map((b, idx) => {
      const remaining = Math.max(0, b.endsAt - now);
      return {
        id: b.id,
        name: b.id,
        icon: '✦',
        stacks: 1,
        expiresAt: b.endsAt,
        durationMs: Math.max(remaining, 1000),
        isDebuff: false,
        dispelType: null,
        appliedIndex: idx,
      };
    });
  }
  return [];
}

/* ─────────────────────────────────────────────────────────────────────
 * Sorting.
 * ──────────────────────────────────────────────────────────────────── */

function sortAuras(list: AuraView[], mode: SortMode): AuraView[] {
  const copy = list.slice();
  if (mode === 'alpha') {
    copy.sort((a, b) => a.name.localeCompare(b.name, 'ru'));
  } else if (mode === 'applied') {
    copy.sort((a, b) => a.appliedIndex - b.appliedIndex);
  } else {
    copy.sort((a, b) => a.expiresAt - b.expiresAt);
  }
  return copy;
}

/* ─────────────────────────────────────────────────────────────────────
 * Public API.
 * ──────────────────────────────────────────────────────────────────── */

export interface BuffBarProps {
  /** Optional override anchor — defaults to top-right under minimap. */
  style?: CSSProperties;
}

/**
 * Combined Buff + Debuff bars. Renders nothing until the server pushes
 * any auras for the local player. Place this once near the root of the
 * world overlay; it positions itself.
 */
export function BuffBar({ style }: BuffBarProps = {}) {
  const selfId = useNet((s) => s.selfId);
  const players = useNet((s) => s.players);
  const me = selfId ? players[selfId] : undefined;

  // Tick once a second so countdown rings update.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => (t + 1) & 0xffff), 250);
    return () => window.clearInterval(id);
  }, []);

  const [sortMode, setSortMode] = useState<SortMode>('time');

  const all = useMemo(() => normaliseAuras(me), [me]);
  const buffs = useMemo(
    () => sortAuras(all.filter((a) => !a.isDebuff), sortMode).slice(0, MAX_BUFFS),
    [all, sortMode],
  );
  const debuffs = useMemo(
    () => sortAuras(all.filter((a) => a.isDebuff), sortMode).slice(0, MAX_DEBUFFS),
    [all, sortMode],
  );

  const cycleSort = useCallback(() => {
    setSortMode((m) => (m === 'time' ? 'alpha' : m === 'alpha' ? 'applied' : 'time'));
  }, []);

  if (buffs.length === 0 && debuffs.length === 0) return null;

  return (
    <div
      data-aiwari-buff-bar
      style={{
        position: 'fixed',
        top: 200,
        right: 16,
        zIndex: 30,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 6,
        pointerEvents: 'auto',
        ...style,
      }}
    >
      <SortButton mode={sortMode} onCycle={cycleSort} />
      {buffs.length > 0 && (
        <AuraRow
          auras={buffs}
          iconPx={BUFF_ICON}
          label="Усиления"
          accent="var(--aiwari-gold)"
        />
      )}
      {debuffs.length > 0 && (
        <AuraRow
          auras={debuffs}
          iconPx={DEBUFF_ICON}
          label="Ослабления"
          accent="hsl(0 70% 55%)"
        />
      )}
    </div>
  );
}

export const DebuffBar = BuffBar;

/* ─────────────────────────────────────────────────────────────────────
 * AuraRow — common renderer for the two strips.
 * ──────────────────────────────────────────────────────────────────── */

function AuraRow({
  auras,
  iconPx,
  label,
  accent,
}: {
  auras: AuraView[];
  /** CSS dimension value (e.g. `var(--aiwari-slot)` or `32px`). */
  iconPx: string;
  label: string;
  accent: string;
}) {
  return (
    <div
      role="list"
      aria-label={label}
      style={{
        display: 'flex',
        flexDirection: 'row-reverse',
        gap: 'var(--aiwari-gap)',
        flexWrap: 'wrap',
        justifyContent: 'flex-start',
        // 16 cells × (icon + gap). gap defaults to var(--aiwari-gap).
        maxWidth: `calc(16 * (${iconPx} + var(--aiwari-gap)))`,
      }}
    >
      {auras.map((aura) => (
        <AuraIcon key={aura.id} aura={aura} sizePx={iconPx} accent={accent} />
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────
 * AuraIcon — single cell.
 * ──────────────────────────────────────────────────────────────────── */

function emitCancelBuff(auraId: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(
      new CustomEvent('aiwari-cancel-buff', { detail: { auraId } }),
    );
  } catch {
    /* Older browsers may not support CustomEvent constructor — silently noop. */
  }
}

function AuraIcon({
  aura,
  sizePx,
  accent,
}: {
  aura: AuraView;
  /** CSS dimension value (e.g. `var(--aiwari-slot)` or `32px`). */
  sizePx: string;
  accent: string;
}) {
  const remainingMs = Math.max(0, aura.expiresAt - Date.now());
  const remainingS = remainingMs / 1000;
  const sweepDeg = aura.durationMs > 0
    ? Math.min(360, (remainingMs / aura.durationMs) * 360)
    : 0;

  const handleContext = (evt: React.MouseEvent) => {
    evt.preventDefault();
    if (aura.isDebuff) return;
    emitCancelBuff(aura.id);
  };

  const tooltipLines = [
    aura.name,
    aura.dispelType ? `Тип: ${dispelLabel(aura.dispelType)}` : '',
    `Осталось: ${remainingS.toFixed(remainingS < 10 ? 1 : 0)}с`,
    aura.isDebuff ? '' : 'ПКМ — отменить',
  ].filter(Boolean).join('\n');

  const dispelStripe = aura.dispelType
    ? DISPEL_COLOURS[aura.dispelType]
    : aura.isDebuff
      ? 'hsl(0 70% 55%)'
      : 'transparent';

  return (
    <button
      type="button"
      role="listitem"
      onContextMenu={handleContext}
      title={tooltipLines}
      style={{
        position: 'relative',
        width: sizePx,
        height: sizePx,
        padding: 0,
        background: 'var(--aiwari-frame-stone)',
        border: `1px solid ${accent}`,
        borderRadius: 2,
        cursor: aura.isDebuff ? 'default' : 'context-menu',
        boxShadow: aura.isDebuff
          ? '0 0 4px hsl(0 70% 50% / 0.4)'
          : '0 0 3px hsl(44 100% 49% / 0.3)',
      }}
    >
      {/* Icon glyph */}
      <span
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: `calc(${sizePx} * 0.55)`,
          lineHeight: 1,
          fontFamily: 'var(--aiwari-font-display)',
          color: 'var(--aiwari-fg)',
        }}
      >
        {aura.icon || '✦'}
      </span>

      {/* Time-remaining ring — same sweep direction as ActionBar CD overlay. */}
      {aura.durationMs > 0 && remainingMs > 0 && (
        <span
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            background: `conic-gradient(rgba(0,0,0,0.55) 0deg ${360 - sweepDeg}deg, transparent ${360 - sweepDeg}deg 360deg)`,
            pointerEvents: 'none',
            transform: 'scaleX(-1) rotate(-180deg)',
            transformOrigin: 'center',
          }}
        />
      )}

      {/* Dispel-type stripe (left edge) */}
      {dispelStripe !== 'transparent' && (
        <span
          aria-hidden
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 2,
            background: dispelStripe,
            pointerEvents: 'none',
          }}
        />
      )}

      {/* Stack count */}
      {aura.stacks > 1 && (
        <span
          aria-hidden
          style={{
            position: 'absolute',
            top: -4,
            right: -4,
            minWidth: 14,
            height: 14,
            padding: '0 3px',
            background: 'var(--aiwari-gold)',
            color: '#0F0900',
            border: '1px solid var(--aiwari-frame-edge)',
            borderRadius: 7,
            fontFamily: 'var(--aiwari-font-mono)',
            fontSize: 'var(--aiwari-text-xs)',
            fontWeight: 700,
            lineHeight: '13px',
            textAlign: 'center',
            pointerEvents: 'none',
          }}
        >
          {aura.stacks}
        </span>
      )}

      {/* Numeric remaining seconds bottom */}
      {remainingS > 0 && remainingS < 60 && (
        <span
          aria-hidden
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: -2,
            textAlign: 'center',
            fontFamily: 'var(--aiwari-font-mono)',
            fontSize: 'var(--aiwari-text-xs)',
            color: 'var(--aiwari-gold-soft)',
            textShadow: '0 1px 0 #000',
            pointerEvents: 'none',
          }}
        >
          {remainingS < 10 ? remainingS.toFixed(1) : remainingS.toFixed(0)}с
        </span>
      )}
    </button>
  );
}

function dispelLabel(t: NonNullable<DispelType>): string {
  switch (t) {
    case 'magic': return 'Магия';
    case 'curse': return 'Проклятие';
    case 'disease': return 'Болезнь';
    case 'poison': return 'Яд';
    case 'physical': return 'Физический';
    case 'enrage': return 'Ярость';
    default: return '';
  }
}

/* ─────────────────────────────────────────────────────────────────────
 * Sort-mode cycle button.
 * ──────────────────────────────────────────────────────────────────── */

function SortButton({ mode, onCycle }: { mode: SortMode; onCycle: () => void }) {
  return (
    <button
      type="button"
      onClick={onCycle}
      title={`Сортировка: ${SORT_LABEL[mode]} — кликни, чтобы сменить.`}
      style={{
        padding: '2px 8px',
        fontFamily: 'var(--aiwari-font-mono)',
        fontSize: 'var(--aiwari-text-sm)',
        background: 'hsl(0 0% 0% / 0.6)',
        border: '1px solid var(--aiwari-frame-edge)',
        color: 'var(--aiwari-fg-muted)',
        borderRadius: 2,
        cursor: 'pointer',
      }}
    >
      ⇅ {SORT_LABEL[mode]}
    </button>
  );
}

export default BuffBar;
